import { observer } from 'mobx-react'
import styles from './PricingPageMobile.module.less'
import { commonUtils } from '@/helpers/commonUtils'
import AIcon from '@/components/A/AIcon/AIcon'
import MiddleText from '@/components/MiddleText/MiddleText'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRequest } from 'alova/client'
import { api } from '@/api'
import { PAGE_LINKS, ROOT_CONTAINER_ID } from '@/router/pageConst'
import { userService } from '@/services/userService'
import { subscribeService } from '@/services/subscribeService'
import cls from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { INTERNAL_HOST } from '@/commonConst'
import { message } from 'antd'

type SecondaryTabKey = 'quick' | 'diagnosis' | 'insight'
const QUICK_STARTER_CATALOG_KEY = 'QuickStarter'
const ENABLE_FASTSPRING_CHECKOUT = false

function PricingPageMobile() {
  const { t, i18n } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  const isZhLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh')
  const isEnLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('en')
  const isJaLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('ja')
  const perTestUnit = t('pricing.premium.time', { defaultValue: isZhLocale ? '次' : 'time' })
  const formatNumber = (value: string | number) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US')
    }
    return value
  }
  const formatCurrency = (value: string | number) => (isInternal ? `CNY¥${formatNumber(value)}` : `US$${formatNumber(value)}`)
  const formatPricePerTest = (value: string | number) => {
    const currencyText = `${isInternal ? '¥' : '$'}${formatNumber(value)}`
    if (isJaLocale) return currencyText
    if (isEnLocale) return `${currencyText} per ${perTestUnit}`
    return `${currencyText}/${perTestUnit}`
  }
  const navigate = useNavigate()
  const pricingBoxRef = useRef<HTMLDivElement>(null)
  const unlimitedBoxRef = useRef<HTMLDivElement>(null)
  const secondaryDropdownRef = useRef<HTMLDivElement>(null)
  const [primaryTab, setPrimaryTab] = useState<'landscape' | 'portrait'>('landscape')
  const [secondaryTab, setSecondaryTab] = useState<SecondaryTabKey>('quick')
  const [secondaryOpen, setSecondaryOpen] = useState(false)
  const isQuickPricing = secondaryTab === 'quick'

  const browserType = commonUtils.getMobileBrowserType()
  const platformType = commonUtils.getMobilePlatformType()

  const { data: planData } = useRequest(api.pricing.getPlanList)
  const starterPlan = planData?.catalogs?.Starter
  const quickStarterPlan = planData?.catalogs?.[QUICK_STARTER_CATALOG_KEY]
  const activeStarterPlan = isQuickPricing ? quickStarterPlan : starterPlan
  const starterPrice = activeStarterPlan?.price
  const starterAnalyseCount = activeStarterPlan?.adAnalyseCount
  const starterOriginalPrice = isQuickPricing
    ? quickStarterPlan?.originalPrice ||
      (quickStarterPlan?.price ? quickStarterPlan.price * 2 : undefined)
    : starterPrice
      ? starterPrice * 2
      : undefined
  const starterOriginalPriceText = !starterOriginalPrice
    ? '-'
    : formatCurrency(starterOriginalPrice)
  const starterPricePerTest = starterPrice && starterAnalyseCount
    ? Math.floor(starterPrice / starterAnalyseCount)
    : '-'
  const starterPricePerTestDesc = starterPricePerTest === '-'
    ? '-'
    : formatPricePerTest(starterPricePerTest)
  const starterCardDesc = [
    t('pricing.starter.cardDesc1', {
      pricePerTest: starterPricePerTestDesc,
      defaultValue: '限时折扣，仅需{{pricePerTest}}',
    }),
    t('pricing.starter.cardDesc2', { defaultValue: '次数永久有效' }),
  ]
  const quickUnlimitedPrice = isInternal ? '50,000' : '7,250'
  const quickUnlimitedTestCount = '500'
  const quickUnlimitedPricePerTest = isInternal ? 100 : 14.5
  const unlimitedCardDesc = [
    t('pricing.unlimited.cardDesc1', {
      pricePerTest: isQuickPricing
        ? formatPricePerTest(quickUnlimitedPricePerTest || '-')
        : isInternal
          ? formatPricePerTest(200)
          : formatPricePerTest(29.95),
      defaultValue: '低至{{pricePerTest}}',
    }),
    t('pricing.unlimited.cardDesc2', { defaultValue: '次数有效期为一年' }),
    t('pricing.unlimited.cardDesc3', { defaultValue: '定制自有基准值' }),
  ]
  const customPlanPrefix = t('pricing.benefits.customPlanPrefix', {
    defaultValue: '我们支持多种灵活与定制化方案——欢迎联系我们',
  })
  const customPlanSuffix = t('pricing.benefits.customPlanSuffix', {
    defaultValue: '获取专属方案',
  })
  const secondaryTabs = primaryTab === 'landscape'
    ? [
      { key: 'quick' as const, label: t('productSwitch.modal.cards.tvcQuick.title'), icon: 'rocket' },
      { key: 'diagnosis' as const, label: t('productSwitch.modal.cards.tvcBase.title'), icon: 'video' },
      { key: 'insight' as const, label: t('productSwitch.modal.cards.tvcSurvey.title'), icon: 'brain' },
    ]
    : [
      { key: 'quick' as const, label: t('productSwitch.modal.cards.portraitQuick.title'), icon: 'rocket' },
      { key: 'diagnosis' as const, label: t('productSwitch.modal.cards.portraitBase.title'), icon: 'video' },
      { key: 'insight' as const, label: t('productSwitch.modal.cards.portraitSurvey.title'), icon: 'brain' },
    ]
  const selectedSecondary = secondaryTabs.find((tab) => tab.key === secondaryTab) || secondaryTabs[0]
  const unlimitedPrice = isQuickPricing ? quickUnlimitedPrice : isInternal ? '200,000' : '29,950'
  const unlimitedTestCount = isQuickPricing ? quickUnlimitedTestCount : '1,000'
  const unlimitedPlanDesc = isQuickPricing
    ? t('pricing.unlimited.planDesc500', {
      defaultValue: isZhLocale ? '全年畅享500次测试' : 'Enjoy up to 500 tests throughout the year',
    })
    : t('pricing.unlimited.planDesc1000', { defaultValue: '全年畅享1,000次测试' })

  const handleContactUs = () => {
    navigate(PAGE_LINKS.contact)
  }

  const dataPopupClosed = () => {
    console.log('dataPopupClosed')
  }
  const dataPopupWebhookReceived = () => {
    console.log('dataPopupWebhookReceived')
  }
  const dataErrorCallback = () => {
    message.error('call fastspringerror')
  }

  const handleSubscribe = () => {
    if (isQuickPricing) {
      handleContactUs()
      return
    }
    if (isInternal) {
      handleContactUs()
      return
    }
    if (!userService.userInfo) {
      navigate(PAGE_LINKS.login)
      return
    }

    const plan = starterPlan
    if (!plan?.planId) {
      message.error('Plan is unavailable, please try again later.')
      return
    }
    subscribeService.subscribePlan(plan.planId, {
      dataPopupClosed,
      dataPopupWebhookReceived,
      dataErrorCallback,
    })
  }

  useEffect(() => {
    const premiumRect = unlimitedBoxRef.current?.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    if (premiumRect) {
      const { left, width } = premiumRect
      const offset = (viewportWidth - width) / 2
      const needScrollLeft = left - offset
      pricingBoxRef.current?.scrollTo({
        left: needScrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  useEffect(() => {
    setSecondaryTab('quick')
    setSecondaryOpen(false)
  }, [primaryTab])

  useEffect(() => {
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      if (!secondaryDropdownRef.current?.contains(e.target as Node)) {
        setSecondaryOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('touchstart', onDocClick)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('touchstart', onDocClick)
    }
  }, [])

  useEffect(() => {
    const rootEl = document.getElementById(ROOT_CONTAINER_ID)
    const closeDropdown = () => setSecondaryOpen(false)
    rootEl?.addEventListener('scroll', closeDropdown)
    return () => {
      rootEl?.removeEventListener('scroll', closeDropdown)
    }
  }, [])


  useEffect(() => {
    /** NOTE: 处理从首页跳转过来时候，页面滚动高度不会重置的问题 */
    const el = document.getElementById(ROOT_CONTAINER_ID)
    if (el) {
      el.scrollTo({
        top: 0,
      })
    }
  }, [])



  return (
    <div className={styles.mobilePricingPage} data-testid='pricing-page'>
      <div className={cls(styles.mobilePricingPageContent,
        browserType === 'firefox' && styles.firefox,
        platformType === 'ios' && styles.ios,
      )}>
        <img src={commonUtils.getImagePath('pricingPageMobile/pricing-page-bg.png')} alt='' />
        <p className={styles.contentTitle}>
          <span className={styles.weight}>AdEff - </span>
          {t('pricing.title')}
        </p>
        <div className={styles.mobileTabSticky}>
          <div className={styles.mobilePrimaryTab}>
            <button
              data-testid='pricing-primary-tab-landscape'
              className={cls(styles.mobilePrimaryTabItem, primaryTab === 'landscape' && styles.mobilePrimaryTabItemActive)}
              onClick={() => setPrimaryTab('landscape')}
            >
              {t('productSwitch.modal.tabs.tvc')}
            </button>
            <button
              data-testid='pricing-primary-tab-portrait'
              className={cls(styles.mobilePrimaryTabItem, primaryTab === 'portrait' && styles.mobilePrimaryTabItemActive)}
              onClick={() => setPrimaryTab('portrait')}
            >
              {t('productSwitch.modal.tabs.portrait')}
            </button>
          </div>
          <div className={styles.mobileSecondarySelect} ref={secondaryDropdownRef}>
            <button
              data-testid='pricing-secondary-trigger'
              className={cls(styles.mobileSecondaryTrigger, secondaryOpen && styles.mobileSecondaryTriggerOpen)}
              onClick={() => setSecondaryOpen((v) => !v)}
            >
              <img
                className={styles.mobileSecondaryIcon}
                src={commonUtils.getImagePath(`productSwitchModal/${selectedSecondary.icon}.png`)}
                alt=''
              />
              <span className={styles.mobileSecondaryLabel}>{selectedSecondary.label}</span>
              <AIcon
                className={cls(styles.mobileSecondaryArrow, secondaryOpen && styles.mobileSecondaryArrowOpen)}
                type='icon-line-global_down'
              />
            </button>
            {secondaryOpen && (
              <div className={styles.mobileSecondaryDropdown}>
                {secondaryTabs.map((tab) => {
                  const isActive = tab.key === secondaryTab
                  const iconName = isActive ? tab.icon : `${tab.icon}-disabled`
                  return (
                    <button
                      key={tab.key}
                      data-testid={`pricing-secondary-option-${tab.key}`}
                      className={cls(styles.mobileSecondaryOption, isActive && styles.mobileSecondaryOptionActive)}
                      onClick={() => {
                        setSecondaryTab(tab.key)
                        setSecondaryOpen(false)
                      }}
                    >
                      <img
                        className={styles.mobileSecondaryIcon}
                        src={commonUtils.getImagePath(`productSwitchModal/${iconName}.png`)}
                        alt=''
                      />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div className={styles.pricingBox} ref={pricingBoxRef}>
          <div className={styles.starter}>
            <div className={styles.pricingItem}>
              <AIcon type='icon-fill-global_Premium' />
              <span className={styles.itemTitle}>
                <MiddleText text={t('pricing.starter.title')} />
              </span>
            </div>
            {
              isInternal
                ? <p className={styles.starterDesc}>{t('pricing.starter.desc_2')}</p>
                : <p className={styles.starterDesc}>{t('pricing.starter.desc_1')}</p>
            }
            <div className={styles.price}>
              {formatCurrency(starterPrice || '-')}
              <div className={styles.discount}>{t('pricing.starter.discount')}</div>
            </div>
            {
              <div className={styles.originPrice}>
                {t('pricing.starter.originPrice')}{starterOriginalPriceText}
              </div>
            }
            <p className={styles.times}>
              <Trans
                i18nKey={isInternal ? 'pricing.starter.timesDesc1' : 'pricing.starter.timesDesc'}
                values={{ count: starterAnalyseCount || '-' }}
                components={{
                  strong: <span className={styles.cnt} />,
                }}
              />
            </p>
            <div className={styles.descBox}>
              {starterCardDesc.map((desc) => {
                return (
                  <div key={desc} className={styles.row}>
                    <span className={styles.descDot}></span>
                    <div>{desc}</div>
                  </div>
                )
              })}
            </div>
            <div
              className={styles.subscribeBtn}
              onClick={ENABLE_FASTSPRING_CHECKOUT ? handleSubscribe : handleContactUs}
              data-testid='pricing-subscribe-btn'
            >
              <MiddleText
                text={ENABLE_FASTSPRING_CHECKOUT && !isInternal && !isQuickPricing
                  ? t('pricing.subscribeBtn')
                  : t('pricing.contactUs')}
              />
              <AIcon className={styles.icon} type='icon-line-global_arrow'></AIcon>
            </div>
          </div>
          <div className={styles.unlimited} ref={unlimitedBoxRef}>
            <div className={styles.pricingItem}>
              <AIcon type='icon-fill-global_Unlimited' />
              <span className={styles.itemTitle}>
                <MiddleText text={t('pricing.unlimited.title')} />
              </span>
            </div>
            <p className={cls(styles.starterDesc, styles[i18n.language])}>
              {unlimitedPlanDesc}
            </p>
            <div className={styles.price}>{formatCurrency(unlimitedPrice || '-')}</div>
            <p className={styles.times}>
              <Trans
                i18nKey='pricing.starter.timesDesc1'
                values={{ count: unlimitedTestCount || '-' }}
                components={{
                  strong: <span className={styles.cnt} />,
                }}
              />
            </p>
            <div className={styles.descBox}>
              {unlimitedCardDesc.map((desc) => {
                return (
                  <div key={desc} className={styles.row}>
                    <span className={styles.descDot}></span>
                    <div>{desc}</div>
                  </div>
                )
              })}
            </div>
            <div className={styles.subscribeBtn} onClick={handleContactUs} data-testid='pricing-contact-us-btn'>
              <MiddleText text={t('pricing.contactUs')} />
              <AIcon className={styles.icon} type='icon-line-global_arrow'></AIcon>
            </div>
          </div>
        </div>
        <div className={styles.customPlanSection}>
          <p className={styles.customPlanTitle}>
            {t('pricing.benefits.moreNeedsTitle', { defaultValue: '有其他购买需求？' })}
          </p>
          <p className={styles.customPlanDesc}>
            {customPlanPrefix}
            <span className={styles.customPlanEmail}> Adeff@mininglamp.com </span>
            {customPlanSuffix}
          </p>
        </div>
      </div>
    </div>
  )
}

export default observer(PricingPageMobile)
